import '../../constants/const.css';
import { COLOR_CODE, LINE_SEQ } from '../../constants/const';
import React, { useEffect, useRef } from 'react';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ChartOptions, ChartDataset } from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

interface pitProps {
  dayCount: number;
  autoDraft: number[][];
  lineTitles: string[];
  isSelect: boolean[];
  exist?: boolean[];
}

const FONT_SIZE_14: number = 14;
const COLOR_LINE_WIDTH: number = 2;
const GRID_LINE_WIDTH: number = 0.2;
const months: number[] = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

const COLOR_ARRAY: string[] = [COLOR_CODE.PLAN, COLOR_CODE.RESULT, COLOR_CODE.DRAFT1, COLOR_CODE.DRAFT2, COLOR_CODE.DRAFT3, COLOR_CODE.DRAFT4, COLOR_CODE.DRAFT5];

function getMaxLimit(values: number[][]): number {
  let maxValue: number = 0;
  for (let i = 0; i < values.length; i++) {
    maxValue = maxValue > Math.max(...values[i]) ? maxValue : Math.max(...values[i]);
  }
  return calcMathCeil(maxValue);
}

function calcMathCeil(value: number) {
  if (value >= 1) {
    const num: number = Math.ceil(value);
    const numLength: number = String(num).length;
    const val1: number = num / 10 ** (numLength - 1);
    const val2: number = Math.ceil(val1);
    if (val1 === val2) {
      return (val2 + 1) * 10 ** (numLength - 1);
    } else {
      return val2 * 10 ** (numLength - 1);
    }
  } else {
    return 1;
  }
}

const ElectricGraph: React.FC<pitProps> = (pitProps) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');

      if (ctx) {
        // 既存のチャートインスタンスが存在する場合、破棄する
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }

        const labelStr: string[] = [];

        labelStr.push('');
        for (let i = 0; i < pitProps.dayCount; i++) {
          if (pitProps.dayCount === months.length) {
            labelStr.push(String(months[i]).concat('月'));
          } else {
            labelStr.push(String(i + 1).concat('日'));
          }
        }

        const tempDatasets: ChartDataset<'line'>[] = [];
        for (let num = 0; num < pitProps.autoDraft.length; num++) {
          let isSet: boolean = true;
          if (pitProps.exist && (num === LINE_SEQ.PLAN || num === LINE_SEQ.RESULT)) {
            if (!pitProps.exist[num]) {
              isSet = false;
            }
          }
          if (isSet) {
            tempDatasets.push({
              label: pitProps.lineTitles[num],
              data: [null, ...pitProps.autoDraft[num]],
              borderColor: COLOR_ARRAY[num],
              backgroundColor: COLOR_ARRAY[num],
              borderWidth: COLOR_LINE_WIDTH,
              tension: 0.1,
              pointRadius: 5,
              hidden: !pitProps.isSelect[num],
              pointStyle: num === LINE_SEQ.PLAN ? 'rectRot' : num === LINE_SEQ.RESULT ? 'crossRot' : 'triangle',
            });
          }
        }

        const data = { labels: labelStr, datasets: tempDatasets };

        const options: ChartOptions<'line'> = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                font: {
                  size: FONT_SIZE_14,
                },
                maxRotation: 0,
                minRotation: 0,
              },
              grid: {
                color: 'black',
                drawTicks: false,
                lineWidth: GRID_LINE_WIDTH,
              },
            },
            y: {
              beginAtZero: true,
              ticks: {
                font: {
                  size: FONT_SIZE_14,
                },
                callback: function (value) {
                  return `${value}[MWh]`;
                },
              },
              grid: {
                color: 'black',
                drawTicks: false,
                lineWidth: GRID_LINE_WIDTH,
              },
              max: getMaxLimit(pitProps.autoDraft),
            },
          },
        };

        // 新しいチャートを作成して、参照を保持する
        chartInstanceRef.current = new Chart(ctx, {
          type: 'line',
          data: data,
          options: options,
        });
      }
    }

    // クリーンアップ時にチャートを破棄する
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [pitProps.dayCount, pitProps.autoDraft, pitProps.lineTitles, pitProps.isSelect]);

  return <canvas ref={chartRef} style={{ maxHeight: '250px', minHeight: '250px' }} />;
};

export default ElectricGraph;
